import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema, backgroundStyleEnum } from "../globalProps";
import { SectionShell } from "../SectionShell";

/* ---------- Inline SVG icon map (no external deps) ---------- */

/**
 * Simple SVG icons keyed by name. Avoids importing lucide-react into
 * common/sections which is shared between portal and frontend.
 */
function ContactIcon({ name, className }: { name: string; className?: string }) {
  const size = 20;
  const svgProps = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    className,
  };

  switch (name) {
    case "phone":
      return (
        <svg {...svgProps}>
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
        </svg>
      );
    case "mail":
      return (
        <svg {...svgProps}>
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="M22 7l-10 7L2 7" />
        </svg>
      );
    case "clock":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      );
    case "map-pin":
      return (
        <svg {...svgProps}>
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      );
    default:
      // Fallback: render the icon name as text
      return (
        <span className={`text-sm font-medium ${className || ""}`}>
          {name}
        </span>
      );
  }
}

/* ---------- Schema ---------- */

const contactItemSchema = z.object({
  label: z.string().default("Label"),
  value: z.string().default("Value"),
  href: z.string().optional(),
  icon: z.string().optional(),
});

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Get in Touch"),
  subheading: z
    .string()
    .default(
      "We would love to hear from you. Reach out using any of the methods below.",
    ),
  items: z
    .array(contactItemSchema)
    .default([
      { label: "Phone", value: "(555) 123-4567", href: "tel:+15551234567", icon: "phone" },
      { label: "Email", value: "info@example.com", href: "mailto:info@example.com", icon: "mail" },
      { label: "Office Hours", value: "Monday -- Friday, 9:00 AM -- 5:00 PM", icon: "clock" },
      { label: "Address", value: "123 Main Street, Suite 200\nAnytown, ST 10001", icon: "map-pin" },
    ]),
  layout: z.enum(["list", "grid"]).default("list"),
  backgroundStyle: backgroundStyleEnum.default("light"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

/* ---------- Shared item renderer ---------- */

function ContactItem({
  item,
  index,
  card,
}: {
  item: z.infer<typeof contactItemSchema>;
  index: number;
  /** Render in card style (centered, with bg) vs. row style */
  card?: boolean;
}) {
  const valueLines = item.value.split("\n");
  const inner = (
    <>
      {item.icon && (
        <div
          className={
            card
              ? "w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-3"
              : "w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0"
          }
        >
          <ContactIcon name={item.icon} />
        </div>
      )}
      <div className={card ? "text-center" : ""}>
        <RichText
          as="div"
          html={item.label}
          className="text-sm font-semibold mb-1"
          data-prop={`items.${index}.label`}
        />
        <div className="text-base opacity-80">
          {valueLines.map((line, li) => (
            <span key={li}>
              {li > 0 && <br />}
              <RichText
                html={line}
                data-prop={li === 0 ? `items.${index}.value` : undefined}
              />
            </span>
          ))}
        </div>
      </div>
    </>
  );

  if (item.href) {
    return (
      <a
        href={item.href}
        className={`no-underline text-inherit hover:opacity-80 transition-opacity ${
          card
            ? "flex flex-col items-center p-6 rounded-xl bg-surface/50"
            : "flex items-center gap-4"
        }`}
      >
        {inner}
      </a>
    );
  }

  return (
    <div
      className={
        card
          ? "flex flex-col items-center p-6 rounded-xl bg-surface/50"
          : "flex items-center gap-4"
      }
    >
      {inner}
    </div>
  );
}

/* ---------- Main component ---------- */

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;

  if (safe.items.length === 0) return null;

  return (
    <SectionShell globals={safe} widthScale="narrow">
        <div className="text-center mb-12">
          <RichText
            as={safe.headingLevel}
            html={safe.heading}
            className="text-3xl @md:text-[2.5rem] font-bold mb-4"
            data-prop="heading"
          />
          {safe.subheading && (
            <RichText
              as="div"
              html={safe.subheading}
              className="text-lg opacity-80"
              data-prop="subheading"
            />
          )}
        </div>

        {safe.layout === "list" ? (
          <div className="flex flex-col gap-6">
            {safe.items.map((item, i) => (
              <ContactItem key={i} item={item} index={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 @md:grid-cols-2 gap-4">
            {safe.items.map((item, i) => (
              <ContactItem key={i} item={item} index={i} card />
            ))}
          </div>
        )}
    </SectionShell>
  );
}
